import React, { ReactElement, useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { IBroadcastFormRecipientResponseReportRow } from 'types/BroadcastForm'

interface SurveyResponseDistributionChartProps {
  filteredRecipients: IBroadcastFormRecipientResponseReportRow[]
}

interface QuestionData {
  questionTitle: string
  [key: string]: string | number
}

const COLORS = [
  '#dc2626', // red-600 (lowest/bad)
  '#ea580c', // orange-600
  '#f59e0b', // amber-500
  '#eab308', // yellow-500
  '#84cc16', // lime-500
  '#22c55e', // green-500
  '#10b981', // emerald-500
  '#14b8a6', // teal-500
  '#06b6d4', // cyan-500
  '#3b82f6', // blue-500
  '#6366f1', // indigo-500
  '#8b5cf6', // violet-500 (highest/good)
]

// Scale types that we want to chart
const chartedScaleTypes = [
  'Likert Scale',
  'Net Promoter',
  'Multi-Select',
  'Custom',
]

// Custom label component that only shows in the center
const CustomLabel = (props: any) => {
  const {
    x, y, width, height, value,
  } = props

  if (value == null || value === 0) return null

  const percentage = value
  if (percentage < 1) return null

  // Only show label if the bar segment is wide enough to display text without clipping
  // Minimum width of 32 pixels should be enough for percentage text
  const MIN_WIDTH = 32
  if (width < MIN_WIDTH) return null

  // Only show label if we're in the center of the bar segment
  // For stacked bars, we check if we're roughly in the middle
  const labelX = x + width / 2
  const labelY = y + height / 2

  return (
    <text
      x={labelX}
      y={labelY}
      fill="#ffffff"
      fontSize={12}
      dominantBaseline="middle"
      textAnchor="middle"
    >
      {`${parseFloat(percentage.toFixed(1))}%`}
    </text>
  )
}

// Custom tooltip component that shows answer text with percentages
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  return (
    <div
      className="bg-white border rounded shadow-lg p-3"
      style={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <p className="font-weight-bold mb-2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
        {label}
      </p>
      {payload.map((entry: any) => {
        if (entry.value == null || entry.value === 0) return null

        return (
          <p key={entry.name || entry.dataKey} style={{ margin: '4px 0', color: entry.color }}>
            <span style={{ fontWeight: 'bold' }}>
              {entry.name}
              :
            </span>
            {' '}
            {entry.value.toFixed(1)}
            %
          </p>
        )
      })}
    </div>
  )
}

export const SurveyResponseDistributionChart: React.FC<SurveyResponseDistributionChartProps> = ({
  filteredRecipients,
}): ReactElement => {
  const chartData = useMemo(() => {
    // Filter to only responses with actual question data
    const questionResponses = filteredRecipients.filter(
      (r) => r.HasResponse && r.QuestionId != null && r.QuestionText,
    )

    // Group responses by question
    // Store both count and option value for sorting
    interface AnswerData {
      count: number
      optionValue?: number
    }
    const questionMap = new Map<number, Map<string, AnswerData>>()

    questionResponses.forEach((response) => {
      // Filter out certain scale types if needed
      if (!chartedScaleTypes.includes(response.ScaleType)) return

      const questionId = response.QuestionId
      if (!questionId) return

      if (!questionMap.has(questionId)) {
        questionMap.set(questionId, new Map<string, AnswerData>())
      }

      const answerMap = questionMap.get(questionId)
      if (!answerMap) return

      // Determine answer text based on available data
      let answerText: string
      if (response.ResponseText != null && response.ResponseText !== '') {
        answerText = response.ResponseText
      } else if (response.OptionText != null && response.OptionText !== '') {
        answerText = response.OptionText
      } else if (response.OptionValue != null) {
        answerText = response.OptionValue.toString()
      } else {
        answerText = '(No answer)'
      }

      const existing = answerMap.get(answerText)
      if (existing) {
        existing.count += 1
        // Keep the optionValue if it exists (for numeric scales)
        if (response.OptionValue != null && existing.optionValue == null) {
          existing.optionValue = response.OptionValue
        }
      } else {
        answerMap.set(answerText, {
          count: 1,
          optionValue: response.OptionValue ?? undefined,
        })
      }
    })

    // Get all unique answer texts with their option values for sorting
    interface AnswerMetadata {
      answerText: string
      optionValue?: number
    }
    const answerMetadataMap = new Map<string, AnswerMetadata>()
    questionMap.forEach((answerMap) => {
      answerMap.forEach((answerData, answerText) => {
        if (!answerMetadataMap.has(answerText)) {
          answerMetadataMap.set(answerText, {
            answerText,
            optionValue: answerData.optionValue,
          })
        } else {
          // If we already have this answer, prefer keeping optionValue if it exists
          const existing = answerMetadataMap.get(answerText)
          if (existing && answerData.optionValue != null && existing.optionValue == null) {
            existing.optionValue = answerData.optionValue
          }
        }
      })
    })

    // Sort answers by their numeric value (low to high)
    // Answers with OptionValue come first, sorted by value
    // Answers without OptionValue come after, sorted alphabetically
    const sortedAnswers = Array.from(answerMetadataMap.values()).sort((a, b) => {
      // Both have numeric values - sort by value
      if (a.optionValue != null && b.optionValue != null) {
        return a.optionValue - b.optionValue
      }
      // Only a has numeric value - it comes first
      if (a.optionValue != null) {
        return -1
      }
      // Only b has numeric value - it comes first
      if (b.optionValue != null) {
        return 1
      }
      // Try to parse as numbers if both are numeric strings
      const aNum = Number.parseFloat(a.answerText)
      const bNum = Number.parseFloat(b.answerText)
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return aNum - bNum
      }
      // Otherwise sort alphabetically
      return a.answerText.localeCompare(b.answerText)
    })

    const sortedAnswerTexts = sortedAnswers.map((a) => a.answerText)

    // Create a map from answer text to option value for color selection
    const answerToOptionValue = new Map<string, number | undefined>()
    sortedAnswers.forEach((a) => {
      answerToOptionValue.set(a.answerText, a.optionValue)
    })

    // Find min and max option values for normalization

    const minOptionValue = 1
    const maxOptionValue = 10

    // Helper function to get color index from option value
    const getColorIndex = (optionValue: number | undefined, answerText: string): number => {
      if (optionValue != null) {
        // Normalize to 0-1 range, then map to color array index
        const normalized = (optionValue - minOptionValue) / (maxOptionValue - minOptionValue || 1)
        const index = Math.round(normalized * (COLORS.length - 1))
        return Math.max(0, Math.min(index, COLORS.length - 1))
      }
      // For non-numeric answers, try to parse the text
      const parsed = Number.parseFloat(answerText)
      if (!Number.isNaN(parsed)) {
        const normalized = (parsed - minOptionValue) / (maxOptionValue - minOptionValue || 1)
        const index = Math.round(normalized * (COLORS.length - 1))
        return Math.max(0, Math.min(index, COLORS.length - 1))
      }
      // Fallback to index in sorted array
      return sortedAnswerTexts.indexOf(answerText) % COLORS.length
    }

    // Build chart data
    const data: QuestionData[] = []
    const questionIds = Array.from(questionMap.keys())

    questionIds.forEach((questionId) => {
      const answerMap = questionMap.get(questionId)

      if (!answerMap || answerMap.size === 0) return

      // Get question text from first response with this question ID
      const firstResponse = questionResponses.find((r) => r.QuestionId === questionId)
      if (!firstResponse) return

      const questionTitle = firstResponse.QuestionText || `Question ${questionId}`
      const truncatedTitle = questionTitle.length > 80
        ? `${questionTitle.substring(0, 80)}...`
        : questionTitle

      // Calculate total responses for this question
      const total = Array.from(answerMap.values()).reduce((sum, answerData) => sum + answerData.count, 0)
      if (total === 0) return

      const row: QuestionData = {
        questionTitle: truncatedTitle,
      }

      // Normalize counts to percentages, using sorted order
      sortedAnswerTexts.forEach((answerText) => {
        const answerData = answerMap.get(answerText)
        if (answerData) {
          row[answerText] = (answerData.count / total) * 100
        }
      })

      data.push(row)
    })

    return {
      data,
      answerTexts: sortedAnswerTexts,
      getColorIndex,
      answerToOptionValue,
    }
  }, [filteredRecipients])

  if (chartData.data.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        No response data available to display.
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={Math.max(300, chartData.data.length * 80)}>
      <BarChart
        data={chartData.data}
        layout="vertical"
        margin={{
          top: 20, right: 30, left: 20, bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          type="number"
          domain={[0, 100]}
          tickFormatter={(value) => `${value}%`}
        />
        <YAxis
          dataKey="questionTitle"
          type="category"
          width={200}
          style={{
            fontSize: '12px',
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        {chartData.answerTexts.map((answerText) => {
          const optionValue = chartData.answerToOptionValue.get(answerText)
          const colorIndex = chartData.getColorIndex(optionValue, answerText)
          return (
            <Bar
              key={answerText}
              dataKey={answerText}
              stackId="a"
              label={CustomLabel}
              fill={COLORS[colorIndex]}
            />
          )
        })}
      </BarChart>
    </ResponsiveContainer>
  )
}
